/**
 * Single-select entity autocomplete for audit-search (and similar) filters.
 *
 * Searches one of profiles / models / functionals / tasks and returns a
 * resolved `EntityRef` ({ kind, id, urn, label }) the caller maps to a filter
 * param — `subject` / `object` (urn) or an `attributes.*` facet (bare id).
 * Generalized from `components/acl/ActorSearch`.
 *
 * When a value is selected it renders a chip with a clear (×) button; otherwise
 * a debounced, portal-anchored search input + dropdown that escapes
 * scroll-clipped containers. The dropdown opens above the input when there
 * is no room for it below (an input at the foot of a phone's bottom sheet).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faMagnifyingGlass,
  faPuzzlePiece,
  faScrewdriverWrench,
  faSpinner,
  faUser,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { searchProfiles, getAvatarUrl } from '@/api/profiles';
import { searchModels } from '@/api/models';
import { searchFunctionals } from '@/api/functionals';
import { searchTasks } from '@/api/tasks';
import { getLabel } from '@/utils/label';
import ModelIcon from './ModelIcon';
import ApiImage from './ApiImage';

export type EntityKind = 'profile' | 'model' | 'functional' | 'task';

export interface EntityRef {
  kind: EntityKind;
  /** Bare resource id (use for `attributes.*` facet filters). */
  id: string;
  /** `urn:brew:<collection>:<id>` (use for `subject` / `object` filters). */
  urn: string;
  /** Human label for the chip. */
  label: string;
  /** Avatar/icon image URL (profile avatar, model icon) — chip shows it. */
  iconSrc?: string;
}

const COLLECTION: Record<EntityKind, string> = {
  profile: 'profiles',
  model: 'models',
  functional: 'functionals',
  task: 'tasks',
};

const KIND_ICON: Record<EntityKind, IconDefinition> = {
  profile: faUser,
  model: faScrewdriverWrench,
  functional: faPuzzlePiece,
  task: faClipboardList,
};

function entityUrn(kind: EntityKind, id: string): string {
  return `urn:brew:${COLLECTION[kind]}:${id}`;
}

interface Hit {
  id: string;
  label: string;
  iconSrc?: string;
}

async function searchEntities(kind: EntityKind, query: string): Promise<Hit[]> {
  const text = query.trim() === '' ? '*' : query.trim();
  if (kind === 'profile') {
    const r = await searchProfiles({ text, sort: 'metadata.label.value', skip: 0, limit: 25 });
    return (r.data || []).map((p) => ({
      id: p._id,
      label: getLabel(p.metadata?.label as never) || p._id,
      // Avatar endpoint always resolves (falls back to a generated monogram
      // server-side / the picker's initials tile on 404).
      iconSrc: p.$icon || getAvatarUrl(p._id),
    }));
  }
  if (kind === 'model') {
    const r = await searchModels(text, { brief: true, limit: 25 });
    return (r.data || []).map((m) => ({
      id: m._id,
      label: getLabel(m.metadata?.label as never) || m._id,
      iconSrc: m.$icon,
    }));
  }
  if (kind === 'functional') {
    const r = await searchFunctionals({ text });
    return (r || []).slice(0, 25).map((f) => ({ id: f._id, label: getLabel(f.metadata?.label as never) || f._id }));
  }
  const r = await searchTasks({ text, skip: 0, limit: 25 });
  return (r.data || []).map((t) => ({ id: t._id, label: getLabel(t.metadata?.label as never) || t._id }));
}

interface Props {
  kind: EntityKind;
  value: EntityRef | null;
  onChange: (ref: EntityRef | null) => void;
  placeholder?: string;
}

export default function EntityPicker({ kind, value, onChange, placeholder }: Readonly<Props>) {
  const [text, setText] = useState('');
  const [results, setResults] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top?: number; bottom?: number; width: number } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Track the input rect while open so the portalled dropdown follows scroll.
  useLayoutEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async-load: measures the input's rect while open and follows scroll and resize; the dropdown anchor is a measured pixel value
      setAnchor(null);
      return;
    }
    function update() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      // Open upward only when below is too short for the list and above has more room.
      if (below < DROPDOWN_MAX + 8 && r.top > below) {
        setAnchor({ left: r.left, bottom: window.innerHeight - r.top + 4, width: r.width });
      } else {
        setAnchor({ left: r.left, top: r.bottom + 4, width: r.width });
      }
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const run = useCallback(
    async (q: string) => {
      setLoading(true);
      try {
        setResults(await searchEntities(kind, q));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [kind],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // fire-and-forget: run catches a failed search and shows no results
    debounceRef.current = setTimeout(() => { void run(text); }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text, open, run]);

  // Selected → chip with clear button.
  if (value) {
    return (
      <span style={chipStyle} title={`${value.label} (${value.id})`}>
        <PickerIcon kind={kind} iconSrc={value.iconSrc} label={value.label} size={18} />
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value.label}
        </span>
        <button type="button" onClick={() => onChange(null)} style={clearBtnStyle} aria-label="Clear">
          <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
        </button>
      </span>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: '0 1 200px', minWidth: 150 }}>
      <div style={inputWrapStyle}>
        <FontAwesomeIcon icon={faMagnifyingGlass} className="text-[10px]" style={{ opacity: 0.5 }} />
        <input
          type="text"
          value={text}
          placeholder={placeholder ?? `Filter by ${kind}…`}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setOpen(true)}
          style={inputStyle}
        />
        {loading && <FontAwesomeIcon icon={faSpinner} spin className="text-[10px]" style={{ opacity: 0.6 }} />}
      </div>
      {open &&
        anchor &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ ...dropdownStyle, left: anchor.left, top: anchor.top, bottom: anchor.bottom, width: anchor.width }}
            role="none"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {results.length === 0 ? (
              <div style={emptyStyle}>{loading ? 'Searching…' : 'No matches'}</div>
            ) : (
              results.map((hit) => (
                <button
                  key={hit.id}
                  type="button"
                  style={rowStyle}
                  onClick={() => {
                    onChange({
                      kind,
                      id: hit.id,
                      urn: entityUrn(kind, hit.id),
                      label: hit.label,
                      iconSrc: hit.iconSrc,
                    });
                    setText('');
                    setOpen(false);
                  }}
                >
                  <PickerIcon kind={kind} iconSrc={hit.iconSrc} label={hit.label} size={22} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {hit.label}
                  </span>
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ── Per-kind avatar / icon ──────────────────────────────────────────────

function initialsFor(label: string): string {
  const parts = (label || '').trim().split(/\s+/);
  const chars = parts.slice(0, 2).map((p) => p[0] ?? '').join('');
  return (chars || '?').toUpperCase();
}

/** Avatar (profile), model icon, or a glyph tile (functional/task). Profiles
 *  fall back to an initials monogram when the avatar image 404s. */
function PickerIcon({
  kind,
  iconSrc,
  label,
  size,
}: Readonly<{
  kind: EntityKind;
  iconSrc?: string;
  label: string;
  size: number;
}>) {
  const [err, setErr] = useState(false);

  if (kind === 'model') {
    return <ModelIcon src={iconSrc} size={size} radius={6} fallback={faScrewdriverWrench} />;
  }

  if (kind === 'profile' && iconSrc && !err) {
    return (
      <ApiImage
        src={iconSrc}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        onError={() => setErr(true)}
        style={{
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          border: '1px solid rgb(var(--brew-blue-light) / 0.18)',
        }}
      />
    );
  }

  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: kind === 'profile' ? '50%' : 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgb(var(--brew-blue-light) / 0.1)',
        border: '1px solid rgb(var(--brew-blue-light) / 0.18)',
        color: '#9fd0ea',
        fontSize: Math.round(size * 0.42),
        fontWeight: 600,
      }}
      aria-hidden
    >
      {kind === 'profile' ? initialsFor(label) : <FontAwesomeIcon icon={KIND_ICON[kind]} />}
    </span>
  );
}

// ── styles ──────────────────────────────────────────────────────────────

const inputWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 9px',
  borderRadius: 8,
  background: 'rgb(var(--brew-blue-light) / 0.06)',
  border: '1px solid rgb(var(--brew-blue-light) / 0.18)',
  color: '#cde6f5',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: '#e5f3fa',
  fontSize: 12.5,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 6px 4px 10px',
  borderRadius: 8,
  background: 'rgb(var(--brew-blue-light) / 0.12)',
  border: '1px solid rgb(var(--brew-blue-light) / 0.3)',
  color: '#cde6f5',
  fontSize: 12.5,
  fontWeight: 600,
};

const clearBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: 6,
  border: 'none',
  background: 'rgb(var(--brew-blue-light) / 0.12)',
  color: '#cde6f5',
  cursor: 'pointer',
};

/** The dropdown's tallest. */
const DROPDOWN_MAX = 280;

const dropdownStyle: React.CSSProperties = {
  position: 'fixed',
  zIndex: 9999,
  maxHeight: DROPDOWN_MAX,
  overflowY: 'auto',
  borderRadius: 10,
  background: 'rgba(8, 16, 30, 0.98)',
  border: '1px solid rgb(var(--brew-blue-light) / 0.25)',
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
  padding: 4,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '7px 10px',
  borderRadius: 7,
  border: 'none',
  background: 'transparent',
  color: '#d4eaf5',
  fontSize: 12.5,
  cursor: 'pointer',
  textAlign: 'left',
};

const emptyStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 12,
  color: 'rgb(var(--aurora-text-muted) / 0.6)',
};
